import {
  ModalDefaultProps, MultipleConfigOption, TableRow,
} from './types';
import {
  useState, useEffect, useMemo,
  Dispatch,
  SetStateAction,
  CSSProperties,
  ReactNode,
  ReactElement,
} from 'react';
import { toast } from 'react-toastify';
import { Box, Flex } from 'reflexbox';
import { InputSearch } from '../InputSearch';
import { colors } from '../../styles/colors';
import {
  PaginationContainer,
  Page,
  ButtonPage,
  TableContainer,
  TableHead,
  HeaderRow,
  HeaderCell,
  HeaderCellOrder,
  ChevronTop,
  ChevronBottom,
  ChevronLeft,
  ChevronRight,
  TableBody,
  Row,
  DataCell,
  CheckboxStyle,
  ResponsiveTable,
  ActionsContainer,
  ActionButton,
} from './styles';
import { useTranslation } from 'react-i18next';
import { getUserProfile } from '../../helpers/userProfile';
import {
  Checkbox, ModalWindow, Button,
} from '../index';
import { useStateVar } from '../../helpers/useStateVar';
import { multiSelectRenderValue } from '../../helpers/selectSearchRenderValue';
import { SearchInput, Label } from '../../pages/Analysis/styles';
import SelectSearch, { fuzzySearch } from 'react-select-search';
import { useParams } from 'react-router-dom';
import { apiCall } from '../../providers';
import { NewPagination } from '../NewPagination';
import { Edit5Icon, Trash3Icon } from 'icons';

function sortNulls(aIsNull, bIsNull, sortOrder) {
  if (sortOrder === '+') {
    if (aIsNull && !bIsNull) return 5;
    if (!aIsNull && bIsNull) return -5;
  }
  if (sortOrder === '-') {
    if (aIsNull && !bIsNull) return -5;
    if (!aIsNull && bIsNull) return 5;
  }

  return 0;
}

export function dynamicSort(property, sortOrder) {
  return function (a, b) {
    const getVal = (row) => (typeof property.accessor === 'function' ? property.accessor(row) : row[property.accessor]);
    const aa = getVal(a);
    const bb = getVal(b);
    // equal items sort equally
    if (aa === bb) {
      return 0;
    }
    // nulls
    const aIsNull = aa == null;
    const bIsNull = bb == null;
    const nullCompare = sortNulls(aIsNull, bIsNull, sortOrder);
    if (nullCompare !== 0) return nullCompare;
    // otherwise, if we're ascending, lowest sorts first
    if (sortOrder === '+') {
      return aa < bb ? -1 : 1;
    }
    // if descending, highest sorts first
    return aa < bb ? 1 : -1;
  };
}

const TOTAL_ITENS_PAGE_UTILITIES = 18;

export function defaultMultipleConfigOptions(
  t: (key: string) => string,
  supervisorList?: { USER: string; NOME: string; SOBRENOME: string }[],
): MultipleConfigOption[] {
  return [
    {
      value: 'setStatus',
      name: t('definirStatus'),
      modal: {
        kind: 'select',
        name: t('alterarStatusUnidade'),
        label: t('status'),
        placeholder: t('selecionarStatus'),
        options: [
          { name: 'Em Instalação', value: 'installation' },
          { name: 'Em Produção', value: 'operation' },
        ],
        multiple: false,
      },
    },
    {
      value: 'setResponsible',
      name: t('definirResponsavel'),
      modal: {
        kind: 'select',
        name: t('definirResponsavel'),
        label: t('responsavel'),
        placeholder: t('selecioneResponsavel'),
        options: (supervisorList ?? []).map((item) => ({ value: item.USER, name: `${item.NOME} ${item.SOBRENOME}` })),
        multiple: true,
      },
    },
    {
      value: 'setWeeklyReport',
      name: t('definirRelatorioSemanal'),
      modal: {
        kind: 'select',
        name: t('definirRelatorioSemanal'),
        label: t('relatorioSemanal'),
        placeholder: t('selecionarHabilitacao'),
        options: [
          { name: 'Habilitar', value: 'enable' },
          { name: 'Desabilitar', value: 'disable' },
        ],
        multiple: false,
      },
    },
  ];
}

type NewTableProps = {
  columns: {
    name: string;
    value: string;
    accessor?: string;
    checkable?: boolean;
    sortable?: boolean;
    width?: CSSProperties['width'];
    textAlign?: string;
    render?: (row: any) => ReactNode;
  }[];
  data: any;
  pageSize: number;
  noSearchBar?: boolean;
  extraBtns?: JSX.Element[];
  hideEmptyTable?: boolean;
  checkBox?: boolean;
  multipleConfig?: boolean;
  canSelectMultiple?: boolean;
  entityKey?: string;
  multipleConfigOptions?: MultipleConfigOption[];
  onApplyMultipleConfig?: (items: TableRow[], option: string, selected: unknown) => Promise<boolean | void>;
  deleteUnitsOpen?: boolean;
  onDeleteUnits?: (rows: TableRow[]) => Promise<void>;
  // o modal de exclusão nasceu para Unidades; quem passa outra entidade informa os próprios textos
  deleteEmptySelectionText?: string;
  deleteConfirmationText?: string;
  onCloseDeleteUnits?: () => void;
  verifyMobile?: boolean;
  supervisors?: {
    USER: string
    LAST_ACCESS: string
    NOME: string
    SOBRENOME: string
    perfil: string
    PERMS_U: string
    CLIENT_BIND: number
    FULLNAME: string
    clientName: string
    clientIds: number[]
    clientNames: string[]
    unitIds?: number[]
    RG: string
    COMMENTS: string
    PICTURE: string
    CITY_ID: string
    CITY_NAME: string
    STATE_ID: string
    STATE_NAME: string
    IS_ACTIVE: string
  }[],
  renderUnits?: () => void;
  keySearch?: string;
  containerStyle?: CSSProperties;
  tableStyle?: CSSProperties;
  styledPagination?: boolean;
  order?: string;
  setOrder?: Dispatch<SetStateAction<''>>;
  setSort?: Dispatch<SetStateAction<string>>;
  sort?: string;
  actions?: {
    onEdit?: (row: any) => void,
    onDelete?: (row: any) => void,
  };
  variant?: 'striped';
  maxPages?: number;
  changePage?: (newPage: number) => void;
  page?: number;
  dataForPage?: boolean;
  selectAllCheckBox?: (selected: boolean, event) => void;
  isAllSelected?: boolean;
}

type ActionProps<TypeRow> = {
  row: TypeRow,
  onEdit?: (row: TypeRow) => void,
  onDelete?: (row: TypeRow) => void
}

export const NewTable = ({
  columns, data, pageSize, noSearchBar, extraBtns, hideEmptyTable, checkBox, multipleConfig, supervisors, renderUnits, keySearch, deleteUnitsOpen, onDeleteUnits, onCloseDeleteUnits, deleteEmptySelectionText, deleteConfirmationText, verifyMobile, containerStyle, tableStyle, styledPagination, order = '', setOrder, sort = '+', setSort, actions, maxPages,
  dataForPage,
  page,
  changePage,
  variant,
  selectAllCheckBox,
  isAllSelected,
  entityKey = 'UNIT_ID',
  canSelectMultiple,
  multipleConfigOptions,
  onApplyMultipleConfig,
}: NewTableProps): JSX.Element => {
  const { clientId } = useParams<{ clientId: string }>();
  const [profile] = useState(getUserProfile);
  const [orderColum, setOrderColumn] = useState(order);
  const [sortOrder, setSortOrder] = useState(sort);
  const [currentPage, setCurrentPage] = useState(1);
  const canUseMultipleSelection = canSelectMultiple ?? profile.manageAllClients;

  const [searchText, setSearchText] = useState('');
  const [filteredRows, setFilteredRows] = useState(data);
  const [state, render, setState] = useStateVar(() => {
    const state = {
      selectedData: [] as {
        UNIT_ID: number,
        SUPERVISOR_ID: string,
      }[],
      supervisors: [] as { USER: string }[],
      selectOption: '' as string,
      checkedItems: [] as number[],
    };
    return state;
  });
  const { t } = useTranslation();
  const multipleConfigOptionsList = multipleConfigOptions ?? defaultMultipleConfigOptions(t, supervisors);

  function applySelection(rows) {
    return rows.map((row) => ({ ...row, checked: state.checkedItems.includes(row[entityKey]) }));
  }

  useEffect(() => {
    setFilteredRows(applySelection(data));
    setCurrentPage(page ?? 1);
  }, [data]);

  function toogleChecked(item) {
    setFilteredRows(filteredRows.map((doc) => {
      if (item[entityKey] === doc[entityKey]) {
        return ({ ...doc, checked: !doc.checked });
      }
      return doc;
    }));
    if (!state.checkedItems.includes(item[entityKey])) {
      setState({ checkedItems: [...state.checkedItems, item[entityKey]] });
    } else {
      setState({ checkedItems: state.checkedItems.filter((id) => id !== item[entityKey]) });
    }
  }

  function AllChecked(ischecked) {
    setFilteredRows(filteredRows.map((item) => ({ ...item, checked: ischecked })));
    if (ischecked) {
      const notIncludes = [...filteredRows.filter((item) => !state.checkedItems.includes(item[entityKey]))];
      const idsNotIncludes = notIncludes.map((item) => item[entityKey]);
      setState({ checkedItems: [...state.checkedItems, ...idsNotIncludes] });
    } else {
      const includes = filteredRows.map((item) => item[entityKey]);
      const filter = state.checkedItems.filter((item) => !includes.some((id) => id === item));
      setState({ checkedItems: filter });
    }
  }

  function decideData() {
    if (dataForPage) return data;
    return filteredRows.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  }

  const getDataPerPage = useMemo(() => decideData(), [filteredRows, currentPage, pageSize]);

  const chevron = (column: { name: string }) => {
    if (orderColum === column.name) {
      if (sortOrder === '+') {
        return <ChevronTop />;
      }

      return <ChevronBottom />;
    }
  };

  const handleSetOrderColumn = (column) => {
    let newSortOrder = '+';
    if (orderColum === column.name && sortOrder === '+') {
      newSortOrder = '-';
    }

    let sortedRows;
    if (orderColum === column.name && sortOrder === newSortOrder) {
      setOrderColumn('');
      setSortOrder('');
    } else {
      sortedRows = [...filteredRows].sort(dynamicSort(column, newSortOrder));
      setOrderColumn(column.name);
      if (setOrder) setOrder(column.name);
      setSortOrder(newSortOrder);
      if (setSort) setSort(newSortOrder);
    }

    if (currentPage !== 1) {
      setCurrentPage(1);
    }
    setFilteredRows(sortedRows);
  };

  function getObjects(obj, val) {
    const objects: any[] = [];
    for (const i in obj) {
      if (!obj.hasOwnProperty(i)) continue;
      if (obj[i] && typeof obj[i] === 'object') {
        continue;
      } else if (obj[i] && obj[i].toString().toLowerCase().includes(val.toString().toLowerCase())) {
        if (objects.lastIndexOf(obj) === -1) {
          objects.push(obj);
        }
      }
    }
    return objects;
  }

  const onSearch = (e) => {
    setSearchText(e.target.value);

    if (e.target.value) {
      setFilteredRows(applySelection(data.filter((row) => getObjects(row, e.target.value).length)));
    } else {
      setFilteredRows(applySelection(data));
    }
    setOrderColumn('+');
    setSortOrder('');
    setCurrentPage(1);
  };

  const onPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const onNextPage = () => {
    if (currentPage * pageSize < filteredRows.length) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleChangePage = (newPage) => {
    if (newPage >= 1 && newPage <= Math.ceil((maxPages ?? filteredRows.length) / pageSize)) {
      if (changePage) changePage(newPage);
      setCurrentPage(newPage);
    }
  };

  function renderHeaderCell({
    column,
    index,
    variant,
    handleSetOrderColumn,
    chevron,
  }: {
    column: any;
    index: number;
    variant: any;
    handleSetOrderColumn: (column: any) => void;
    chevron: (column: any) => React.ReactNode;
  }) {
    const key = column.name || index;

    const style = {
      width: column.width || 'auto',
      textAlign: 'left' as const,
    };

    if (column.value === 'checkboxFault') {
      return (
        <HeaderCell
          key={key}
          style={{ ...style, textAlign: 'start' }}
          variant={variant}
        >
          <Checkbox
            key="checkbox-all-faults"
            checked={isAllSelected || false}
            onClick={(e) => selectAllCheckBox && selectAllCheckBox(!isAllSelected, e)}
          />
        </HeaderCell>
      );
    }

    if (column.checkable || column.sortable === false) {
      return (
        <HeaderCell
          key={key}
          style={{ ...style, textAlign: 'start' }}
          variant={variant}
        >
          {column.value}
        </HeaderCell>
      );
    }

    return (
      <HeaderCellOrder
        key={key}
        style={style}
        onClick={() => handleSetOrderColumn(column)}
        variant={variant}
      >
        {column.value}
        {chevron(column)}
      </HeaderCellOrder>
    );
  }

  return (
    <>
      <Flex flexWrap="wrap" flexDirection="row" width="100%" justifyContent="space-between">
        {!noSearchBar && (
          <Box minWidth="200px" width={[1, 1, 1, 1, 1 / 5]} mb={[16, 16, 16, 16, 16, 0]}>
            <InputSearch
              id={keySearch || 'search'}
              name="search"
              placeholder={t('pesquisar')}
              value={searchText}
              onChange={onSearch}
            />
          </Box>
        )}
        {
          (multipleConfig && canUseMultipleSelection) && (
            <SearchInput
              style={{
                width: 300, margin: 0, marginRight: 20,
              }}
            >
              <div
                style={{
                  width: '100%',
                  paddingBottom: 4,
                  margin: 0,
                }}
              >
                <Label>{t('multiplasConfiguracoes')}</Label>
                <SelectSearch
                  options={multipleConfigOptionsList}
                  value={state.selectOption}
                  closeOnSelect
                  printOptions="on-focus"
                  search
                  filterOptions={fuzzySearch}
                  placeholder={`${state.checkedItems.length} ${t('selecionados')}`}
                  onChange={(value: any) => { setState({ selectOption: value }); }}
                />
              </div>
            </SearchInput>
          )
        }
        {extraBtns?.length !== 0 && (
          extraBtns?.map((btn) => <Box minWidth="200px" mb={[16, 16, 16, 16, 16, 0]}>{btn}</Box>)
        )}
      </Flex>
      <br />
      {!(hideEmptyTable && data.length === 0) && (
        <>
          <ResponsiveTable verifyMobile={verifyMobile} style={containerStyle} variant={variant}>
            <TableContainer verifyMobile={verifyMobile} style={tableStyle} variant={variant}>
              <TableHead>
                <HeaderRow>
                  {
                    (checkBox && canUseMultipleSelection) && (
                      <CheckedAll filteredRows={filteredRows} state={state} AllChecked={(bool) => AllChecked(bool)} entityKey={entityKey} />
                    )
                  }
                  {columns.map((column, index) => renderHeaderCell({
                    column,
                    index,
                    variant,
                    handleSetOrderColumn,
                    chevron,
                  }))}
                  {!!actions && (
                    <HeaderCell style={{ width: '80px', textAlign: 'center' }} variant={variant}>
                      {t('acoes')}
                    </HeaderCell>
                  )}
                </HeaderRow>
              </TableHead>
              <TableBody variant={variant}>
                {getDataPerPage.map((row, index) => (
                  <Row key={index} variant={variant}>
                    {
                      (checkBox && canUseMultipleSelection) && (
                        <Checked item={row} setFilter={toogleChecked} state={state} entityKey={entityKey} />
                      )
                    }
                    {columns.map((cell) => (
                      <DataCell
                        key={`${cell.name + index}`}
                        style={{
                          width: cell.width ?? 'auto',
                          textAlign: cell.textAlign as CSSProperties['textAlign'] ?? 'left',
                        }}
                        variant={variant}
                      >
                        {cell.render ? cell.render(row) : (cell.accessor ? row[cell.accessor] : '')}
                      </DataCell>
                    ))}
                    {!!actions && (
                      <DataCell key={`actions-${index}`} style={{ width: '80px' }}>
                        <Actions
                          row={row}
                          onDelete={actions.onDelete}
                          onEdit={actions.onEdit}
                        />
                      </DataCell>
                    )}
                  </Row>
                ))}
              </TableBody>
            </TableContainer>
          </ResponsiveTable>
          {
            styledPagination ? (
              <NewPagination
                currentPage={currentPage}
                onPageChange={handleChangePage}
                totalPages={Math.ceil((maxPages ?? filteredRows.length) / pageSize)}
              />
            ) : (
              <Pagination
                currentPage={currentPage}
                onNextPage={onNextPage}
                onPreviousPage={onPreviousPage}
                filteredRows={filteredRows}
                pageSize={pageSize}
              />
            )
          }
        </>
      )}
      {
        multipleConfigOptionsList.some((option) => option.value === state.selectOption) && (
          <ModalDefault
            state={state}
            setState={setState}
            t={t}
            clientId={Number(clientId)}
            renderUnits={renderUnits}
            filteredRows={filteredRows}
            entityKey={entityKey}
            options={multipleConfigOptionsList}
            onApplyMultipleConfig={onApplyMultipleConfig}
          />
        )
      }
      {
        deleteUnitsOpen && (
          <ModalDeleteUnits
            filteredRows={filteredRows}
            onDeleteUnits={onDeleteUnits}
            onCloseDeleteUnits={onCloseDeleteUnits}
            emptySelectionText={deleteEmptySelectionText}
            confirmationText={deleteConfirmationText}
          />
        )
      }
    </>
  );
};

export const Pagination = ({
  currentPage,
  onPreviousPage,
  onNextPage,
  pageSize,
  filteredRows,
}) => (
  <PaginationContainer>
    <ButtonPage
      style={{
        borderTopLeftRadius: 10,
        borderBottomLeftRadius: 10,
        backgroundColor: currentPage > 1 ? colors.Blue300 : colors.Grey,
      }}
      onClick={onPreviousPage}
    >
      <ChevronLeft />
    </ButtonPage>
    <Page>{currentPage}</Page>
    <ButtonPage
      style={{
        borderTopRightRadius: 10,
        borderBottomRightRadius: 10,
        backgroundColor: currentPage * pageSize < filteredRows.length ? colors.Blue300 : colors.Grey,
      }}
      onClick={onNextPage}
    >
      <ChevronRight />
    </ButtonPage>
  </PaginationContainer>
);

function ModalDefault({
  state, setState, t, clientId, renderUnits, filteredRows, entityKey, options, onApplyMultipleConfig,
}: Readonly<ModalDefaultProps>) {
  const [selected, setSelected] = useState<any>();
  const [loading, setLoading] = useState(false);

  const modal = options.find((option) => option.value === state.selectOption)?.modal;

  async function applyUnitsConfig(checkedRows: TableRow[]) {
    const unitConfigType = (['setStatus', 'setWeeklyReport', 'setResponsible'] as const)
      .find((type) => type === state.selectOption);
    if (!unitConfigType) return;

    const blocks = [] as TableRow[][];
    for (let i = 0; i < checkedRows.length; i += 60) {
      blocks.push(checkedRows.slice(i, i + 60));
    }
    await Promise.all(
      blocks.map((block) => apiCall('/clients/multiple-configs-units', {
        UNITS_IDS: block.map((item) => ({ UNIT_ID: item.UNIT_ID, SUPERVISOR_ID: item.SUPERVISOR_ID })),
        TYPE: unitConfigType,
        SELECTED: !selected ? 'clear' : selected,
      })),
    );
    toast.success(t('unidadesAlteradasComSucesso'));
  }

  async function sendItems() {
    if (!selected && !onApplyMultipleConfig) {
      return;
    }
    setLoading(true);
    const checkedRows = filteredRows.filter((item) => state.checkedItems.includes(item[entityKey]));
    if (!checkedRows.length) {
      toast.warn(t('selecioneAoMenosUmItem'));
      setLoading(false);
      return;
    }
    try {
      if (onApplyMultipleConfig) {
        const applied = await onApplyMultipleConfig(checkedRows, state.selectOption, selected);
        if (applied === false) {
          setLoading(false);
          return;
        }
      } else {
        await applyUnitsConfig(checkedRows);
      }
      renderUnits?.();
      setState({ selectOption: '' });
    } catch (err) {
      console.log(err);
      toast.error(t('houveErro'));
      setState({ selectOption: '' });
    }
    setLoading(false);
  }

  return (
    <ModalWindow borderTop style={{ width: 400 }} onClickOutside={() => setState({ selectOption: '' })}>
      <h3 style={{ padding: '0 20px' }}>
        <strong>{modal?.name}</strong>
      </h3>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 10,
        padding: 20,
      }}
      >
        {modal?.kind === 'custom' ? modal.render({ selected, setSelected }) : (
          <SearchInput style={{ margin: 0, zIndex: 10 }}>
            <div style={{ width: '100%', paddingTop: 3, paddingBottom: 3 }}>
              <Label>{modal?.label}</Label>
              <SelectSearch
                options={modal?.options ?? []}
                value={selected}
                printOptions="on-focus"
                search
                filterOptions={fuzzySearch}
                multiple={modal?.multiple}
                closeOnSelect={!modal?.multiple}
                renderValue={multiSelectRenderValue}
                placeholder={modal?.placeholder}
                // eslint-disable-next-line react/jsx-no-bind
                onChange={(value) => { setSelected(value); }}
              />
            </div>
          </SearchInput>
        )}
        {
          state.selectOption === 'setResponsible' && (
            <p
              style={{
                color: 'blue',
                width: '100%',
                textDecoration: 'underline',
                paddingLeft: 3,
              }}
              onClick={() => { setSelected('clear'); sendItems(); }}
            >
              {t('limparResponsavel')}
            </p>
          )
        }
        <Button variant={loading ? 'disabled' : 'primary'} onClick={() => { sendItems(); }}>{t('aplicar')}</Button>
        <p
          style={{ textDecoration: 'underline', color: '#6C6B6B', margin: 0 }}
          onClick={() => setState({ selectOption: '' })}
        >
          {t('cancelar')}
        </p>
      </div>
    </ModalWindow>
  );
}

function ModalDeleteUnits(params: {
  filteredRows: TableRow[],
  onDeleteUnits?: (rows: TableRow[]) => Promise<void>;
  onCloseDeleteUnits?: () => void,
  emptySelectionText?: string,
  confirmationText?: string,
}) {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const {
    onCloseDeleteUnits, onDeleteUnits, filteredRows, emptySelectionText, confirmationText,
  } = params;

  async function sendItems() {
    if (!onDeleteUnits || !onCloseDeleteUnits) return;
    const unitsToDelete = filteredRows?.filter((x) => x.checked);

    if (!unitsToDelete.length) {
      toast.warn(emptySelectionText ?? t('escolhaUnidadesParaExcluir'));
      return;
    }
    try {
      setLoading(true);
      await onDeleteUnits(unitsToDelete);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  }

  return (
    <ModalWindow borderTop style={{ width: 400 }} onClickOutside={onCloseDeleteUnits}>
      <h3 style={{ textAlign: 'center' }}>
        <strong style={{ textTransform: 'uppercase' }}>{t('aviso')}</strong>
      </h3>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 10,
        padding: '10px 20px',
      }}
      >
        <p style={{ fontSize: '15px', fontWeight: '500', margin: '0 22px' }}>{confirmationText ?? t('desejaProsseguirComExclusaoDasUnidadesSelecionadas')}</p>
        <Button variant={loading ? 'disabled' : 'primary'} onClick={() => { sendItems(); }}>{t('prosseguir')}</Button>
        <button
          type="button"
          style={{
            textDecoration: 'underline',
            color: '#6C6B6B',
            cursor: 'pointer',
            background: 'none',
            border: 'none',
            padding: 0,
          }}
          onClick={onCloseDeleteUnits}
        >
          {t('cancelar')}
        </button>
      </div>
    </ModalWindow>
  );
}

function Checked({
  item, setFilter, state, entityKey,
}) {
  const [checked, setChecked] = useState(false);
  function toogleChecked() {
    setChecked(!checked);
    setFilter(item);
  }
  useEffect(() => {
    const object = state.checkedItems.find((id) => item[entityKey] === id);
    setChecked(object || false);
  }, [state.checkedItems]);
  return (
    <DataCell>
      <Checkbox
        checked={checked}
        onClick={() => {
          toogleChecked();
        }}
      />
    </DataCell>
  );
}

function CheckedAll({
  filteredRows, state, AllChecked, entityKey,
}) {
  function isChecked() {
    return !filteredRows.some((item) => !state.checkedItems.some((check) => check === item[entityKey]));
  }
  useEffect(() => {
  }, [state.checkedItems]);
  return (
    <CheckboxStyle
      style={{
        borderRadius: '10px 0 0 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Checkbox
        checked={isChecked()}
        onClick={() => {
          isChecked() ? AllChecked(false) : AllChecked(true);
        }}
      />
    </CheckboxStyle>
  );
}

function Actions<TypeRow>({
  row,
  onDelete,
  onEdit,
} : Readonly<ActionProps<TypeRow>>): ReactElement | null {
  if (!onEdit && !onDelete) return null;

  return (
    <ActionsContainer>
      {!!onEdit && (
        <ActionButton
          type="button"
          variant="info-filled"
          onClick={() => onEdit(row)}
        >
          <Edit5Icon />
        </ActionButton>
      )}
      {!!onDelete && (
        <ActionButton
          type="button"
          variant="danger-filled"
          onClick={() => onDelete(row)}
        >
          <Trash3Icon />
        </ActionButton>
      )}
    </ActionsContainer>
  );
}
